import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, Mail, MapPin, Clock, Star,
  CheckCircle, Menu, X, ChevronRight,
  Truck, Shield, Zap, Leaf, Smartphone,
} from "lucide-react";
import businessConfig from "../config/businessConfig";
import BookingModal   from "../components/BookingModal";
import { useSEO }     from "../hooks/useSEO";
import ServiceCards   from "../components/ServiceCards";
import Footer         from "../components/Footer";

export default function LandingPage() {
  useSEO();
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [bookOpen,   setBookOpen]   = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const [activeTab,  setActiveTab]  = useState(0);
  const [navigating, setNavigating] = useState(false);
  const [loadPct,    setLoadPct]    = useState(0);
  const [blogOpen,   setBlogOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function scrollTo(id) {
    setMenuOpen(false);
    if (navigating) return;
    setNavigating(true);
    setLoadPct(0);
    let pct = 0;
    const interval = setInterval(() => {
      pct += Math.random() * 25;
      if (pct >= 90) { pct = 90; clearInterval(interval); }
      setLoadPct(pct);
    }, 120);
    setTimeout(() => {
      clearInterval(interval);
      setLoadPct(100);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        setTimeout(() => { setNavigating(false); setLoadPct(0); }, 400);
      }, 200);
    }, 1800);
  }

  const navLinks = ["home", "services", "pricing", "how-it-works", "testimonials", "contact"];

  const trustIcons = [
    <Shield    size={18} key="shield"/>,
    <Truck     size={18} key="truck"/>,
    <Zap       size={18} key="zap"/>,
    <Leaf      size={18} key="leaf"/>,
    <Smartphone size={18} key="phone"/>,
  ];

  return (
    <div className="font-sans text-gray-800 bg-white">

      {/* ══ LOADING BAR ═════════════════════════════════════ */}
      {navigating && (
        <>
          <div className="fixed top-0 left-0 z-[100] h-1 bg-indigo-600 transition-all duration-200 rounded-r-full"
            style={{ width: `${loadPct}%` }}/>
          <div className="fixed inset-0 z-[90] bg-white/30 backdrop-blur-[1px] pointer-events-none transition-opacity"/>
          <div className="fixed top-1/2 left-1/2 z-[95] -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3 border border-gray-100">
              <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"/>
              <span className="text-sm font-bold text-gray-700">Loading…</span>
            </div>
          </div>
        </>
      )}

      {/* ══ NAVBAR ══════════════════════════════════════════ */}
      <nav className={`fixed top-0 w-full z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-md" : "shadow-sm"}`}>
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between" style={{ height: "80px" }}>
          <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => scrollTo("home")}>
            <img src={businessConfig.logo} alt={businessConfig.name}
              style={{ height:"125px", width:"auto", minWidth:"100px", maxWidth:"160px", objectFit:"contain", display:"block" }}/>
            {/* ── Brand name: 1464 Country Cleaners ── */}
            <div className="flex flex-col leading-tight">
              <span
                className="font-extrabold tracking-tight text-sm md:text-base"
                style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: "0.02em" }}
              >
                <span style={{ color: "#374151" }}>1464 </span>
                <span style={{ color: "#1d4ed8" }}>Country</span>
                <span style={{ color: "#16a34a" }}> Cleaners</span>
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6b7280" }}>
                Dry Cleaning Service
              </span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-5 text-sm font-medium text-gray-600">
            {navLinks.map(n => (
              <button key={n} onClick={() => scrollTo(n)}
                className="capitalize hover:text-indigo-600 transition-colors font-semibold">
                {n.replace("-", " ")}
              </button>
            ))}
            <button onClick={() => setBlogOpen(true)} className="capitalize hover:text-indigo-600 transition-colors font-semibold">Blog</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setBookOpen(true)}
              className="hidden md:block bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors shadow-sm">
              Book Now
            </button>
            <button className="md:hidden p-2 text-gray-600 hover:text-indigo-600 transition-colors" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={24}/> : <Menu size={24}/>}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t px-4 py-4 flex flex-col gap-3 text-sm shadow-lg">
            {navLinks.map(n => (
              <button key={n} onClick={() => scrollTo(n)}
                className="capitalize text-left text-gray-700 hover:text-indigo-600 py-1.5 font-semibold border-b border-gray-50 last:border-0">
                {n.replace("-", " ")}
              </button>
            ))}
            <button onClick={() => { setMenuOpen(false); setBlogOpen(true); }}
              className="text-left text-gray-700 hover:text-indigo-600 py-1.5 font-semibold border-b border-gray-50">Blog</button>
            <button onClick={() => { setMenuOpen(false); setBookOpen(true); }}
              className="bg-indigo-600 text-white py-3.5 rounded-xl font-black mt-2 text-sm">
              📅 Book a Pickup
            </button>
          </div>
        )}
      </nav>

      {/* ══ HERO ════════════════════════════════════════════ */}
      <section id="home" className="pt-32 md:pt-40 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 text-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center gap-10">
          <div className="flex-1 text-center md:text-left">
            <span className="inline-block bg-white/20 text-xs px-3 py-1 rounded-full font-semibold tracking-wide uppercase mb-4">
              ⭐ #1 Laundry Service in {businessConfig.location}
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">{businessConfig.tagline}</h1>
            <p className="mt-4 text-indigo-100 text-lg max-w-lg mx-auto md:mx-0">{businessConfig.description}</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center md:justify-start">
              <button onClick={() => setBookOpen(true)}
                className="bg-white text-indigo-700 px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                Book a Pickup
              </button>
              <button onClick={() => scrollTo("how-it-works")}
                className="border border-white/40 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all flex items-center gap-2">
                How It Works <ChevronRight size={16}/>
              </button>
            </div>
            <div className="mt-8 flex gap-6 justify-center md:justify-start">
              {businessConfig.stats.map((s, i) => (
                <div key={i}>
                  <div className="font-extrabold text-2xl">{s.number}</div>
                  <div className="text-indigo-200 text-sm">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="w-64 h-64 md:w-80 md:h-80 bg-white/10 rounded-3xl flex flex-col items-center justify-center gap-4 border border-white/20 backdrop-blur-sm">
              <div className="text-7xl">🧺</div>
              <div className="text-center px-4">
                <p className="font-bold text-lg">Ready in 24 Hours</p>
                <p className="text-indigo-200 text-sm mt-1">Free pickup &amp; delivery included</p>
              </div>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="gold" color="gold"/>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TRUST BAND ══════════════════════════════════════ */}
      <div className="bg-indigo-50 py-4 border-y border-indigo-100">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-4 md:gap-8">
          {businessConfig.trustBadges.map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-indigo-700 font-medium">
              <span className="text-indigo-500">{trustIcons[i]}</span>
              {b.text}
            </div>
          ))}
        </div>
      </div>

      {/* ══ SERVICES ════════════════════════════════════════ */}
      <section id="services" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Professional Care for Every Fabric</h2>
            <p className="text-gray-500 mt-3 text-base max-w-xl mx-auto leading-relaxed">
              From everyday wash and fold to delicate dry cleaning — every garment is treated with expert care and returned fresh and on time.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {["🛡️ Fully Insured","🚚 Free Pickup & Delivery","⭐ 4.9 Google Rating","🔁 Satisfaction Guarantee"].map((t, i) => (
                <span key={i} className="bg-gray-50 border border-gray-200 text-gray-600 text-xs font-semibold px-4 py-2 rounded-full">{t}</span>
              ))}
            </div>
            <p className="text-indigo-500 text-xs font-semibold mt-5 animate-pulse">👆 Tap any service to learn more</p>
          </div>
          <ServiceCards setBookOpen={setBookOpen}/>
          <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white">
            <div>
              <h3 className="font-black text-xl mb-1">Not sure which service you need?</h3>
              <p className="text-indigo-100 text-sm">Call us and our experts will recommend the best treatment for your garments — free of charge.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <a href={`tel:${businessConfig.phone}`} className="bg-white text-indigo-700 px-6 py-3 rounded-xl font-black hover:bg-indigo-50 transition-colors text-sm whitespace-nowrap">📞 Call Us</a>
              <button onClick={() => setBookOpen(true)} className="border-2 border-white/40 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/10 transition-colors text-sm whitespace-nowrap">Book Now</button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ════════════════════════════════════ */}
      <section id="how-it-works" className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">How It Works</h2>
            <p className="text-gray-500 mt-3 text-base">Clean clothes in 4 easy steps</p>
            <p className="text-indigo-400 text-xs font-semibold mt-2 animate-pulse">👆 Click the first two steps to get started</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {businessConfig.steps.map((s, i) => {
              const isBookStep    = i === 0;
              const isServiceStep = i === 1;
              const isClickable   = isBookStep || isServiceStep;
              function handleStepClick() {
                if (isBookStep)    { setBookOpen(true); return; }
                if (isServiceStep) { scrollTo("services"); }
              }
              return (
                <div key={i} onClick={isClickable ? handleStepClick : undefined}
                  className={`relative text-center p-6 bg-white rounded-2xl shadow-sm transition-all duration-200 ${isClickable ? "cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:border-indigo-300 border-2 border-transparent group" : "border-2 border-transparent"}`}>
                  {i < businessConfig.steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 -right-3 w-6 h-0.5 bg-indigo-200 z-10"/>
                  )}
                  {isClickable && <div className="absolute inset-0 rounded-2xl bg-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity"/>}
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-3xl mb-4 shadow-md transition-all duration-200 ${isClickable ? "bg-indigo-600 text-white group-hover:bg-indigo-700 group-hover:scale-110" : "bg-indigo-100 text-indigo-400"}`}>
                    {s.icon}
                  </div>
                  <div className="text-xs font-black text-indigo-400 mb-1 tracking-widest">STEP {s.number}</div>
                  <h3 className="font-black text-lg text-gray-900">{s.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{s.desc}</p>
                  {isBookStep    && <div className="mt-4 bg-indigo-600 text-white text-xs font-black px-4 py-2 rounded-xl group-hover:bg-indigo-700 transition-colors inline-block">📅 Book a Pickup Now →</div>}
                  {isServiceStep && <div className="mt-4 bg-indigo-50 text-indigo-600 text-xs font-black px-4 py-2 rounded-xl border border-indigo-200 group-hover:bg-indigo-100 transition-colors inline-block">🧥 View Our Services →</div>}
                  {!isClickable  && <div className="mt-4 text-gray-300 text-xs font-semibold">We handle this for you ✓</div>}
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm mb-4">Ready to experience the difference? It takes less than 60 seconds to book.</p>
            <button onClick={() => setBookOpen(true)} className="bg-indigo-600 text-white px-8 py-3.5 rounded-xl font-black hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg text-sm">
              📅 Book My First Pickup — It is Free
            </button>
          </div>
        </div>
      </section>

      {/* ══ PRICING ═════════════════════════════════════════ */}
      <section id="pricing" className="py-16 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Honest &amp; Transparent Pricing</h2>
            <p className="text-gray-500 mt-3 text-base max-w-xl mx-auto leading-relaxed">
              No hidden fees. No surprises. We believe every customer deserves to know exactly what they are paying for before we pick up a single garment.
            </p>
          </div>

          {/* ── Pricing cards — driven from businessConfig.pricingCards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {businessConfig.pricingCards.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-indigo-200 transition-all text-center">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-indigo-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"/>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"/>
            <div className="relative z-10">
              <span className="inline-block bg-white/20 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">Get Your Price Today</span>
              <h3 className="text-2xl md:text-3xl font-black mb-3">Every Order Is Priced Fairly</h3>
              <p className="text-indigo-100 text-base max-w-lg mx-auto mb-8 leading-relaxed">
                Pricing depends on garment type, quantity and service selected. Book a pickup and we will give you a full quote before we begin — completely free and with no obligation.
              </p>
              {/* ── Updated badges — reflects real policy ── */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {[
                  "📦 25 piece minimum order",
                  "📍 Service within 5–10 miles",
                  "✅ Free pickup & delivery",
                  "✅ Pay after service",
                ].map((item, i) => (
                  <span key={i} className="bg-white/15 text-white text-sm font-semibold px-4 py-2 rounded-xl border border-white/20">{item}</span>
                ))}
              </div>
              {/* ── Service area callout ── */}
              <div className="bg-white/10 border border-white/20 rounded-2xl px-6 py-4 max-w-lg mx-auto mb-8 text-sm text-indigo-100">
                📞 <span className="font-bold text-white">Not sure if we cover your area?</span> Call us at{" "}
                <a href={`tel:${businessConfig.phoneDial}`} className="font-black text-white underline underline-offset-2 hover:text-indigo-200">
                  {businessConfig.phone}
                </a>{" "}
                and we'll confirm in seconds.
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => setBookOpen(true)} className="bg-white text-indigo-700 px-8 py-3.5 rounded-xl font-black hover:bg-indigo-50 transition-colors shadow-lg hover:shadow-xl text-base">Get a Free Quote</button>
                <a href={`tel:${businessConfig.phone}`} className="border-2 border-white/40 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-white/10 transition-colors text-base flex items-center justify-center gap-2">📞 Call Us Now</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ════════════════════════════════════ */}
      <section id="testimonials" className="py-16 bg-indigo-600">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white">What Our Customers Say</h2>
            <p className="text-indigo-200 mt-2">Real reviews from TX customers</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {businessConfig.testimonials.map((t, i) => (
              <button key={i} onClick={() => setActiveTab(i)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeTab === i ? "bg-white text-indigo-700" : "bg-white/20 text-white hover:bg-white/30"}`}>
                {t.name.split(" ")[0]} {t.name.split(" ")[1]}
              </button>
            ))}
          </div>
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur p-8 rounded-3xl text-white text-center border border-white/20">
            <div className="flex justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} fill="gold" color="gold"/>)}
            </div>
            <p className="text-lg text-indigo-100 italic leading-relaxed mb-6">
              "{businessConfig.testimonials[activeTab].text}"
            </p>
            <div className="flex items-center justify-center gap-3">
              <div className="w-11 h-11 rounded-full bg-indigo-400 flex items-center justify-center font-bold text-lg">
                {businessConfig.testimonials[activeTab].name[0]}
              </div>
              <div className="text-left">
                <p className="font-bold">{businessConfig.testimonials[activeTab].name}</p>
                <p className="text-indigo-200 text-sm">{businessConfig.testimonials[activeTab].location}</p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {businessConfig.testimonials.slice(0, 3).map((t, i) => (
              <div key={i} onClick={() => setActiveTab(i)}
                className={`bg-white/10 backdrop-blur p-5 rounded-2xl text-white cursor-pointer transition-all border ${activeTab === i ? "border-white" : "border-white/10 hover:border-white/30"}`}>
                <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(j => <Star key={j} size={12} fill="gold" color="gold"/>)}</div>
                <p className="text-indigo-100 text-sm italic leading-relaxed mb-3">"{t.text.slice(0, 100)}..."</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-400 flex items-center justify-center text-xs font-bold">{t.name[0]}</div>
                  <div>
                    <p className="font-semibold text-xs">{t.name}</p>
                    <p className="text-indigo-300 text-xs">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CONTACT ═════════════════════════════════════════ */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">Contact Us</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">We Are Always Ready to Help</h2>
            <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto">Reach us by phone, email or drop off your garments in person. Pick the option that works best for you.</p>
          </div>
          <div className="max-w-xl mx-auto w-full space-y-3">

            {/* ── CALL ── */}
            <a href={`tel:${businessConfig.phoneDial}`}
              className="flex items-center gap-4 p-4 rounded-2xl bg-indigo-50 border-2 border-transparent hover:border-indigo-300 hover:bg-indigo-100 transition-all group cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md"><Phone className="text-white" size={20}/></div>
              <div className="flex-1">
                <p className="font-black text-sm text-gray-900">Call Us Now</p>
                <p className="text-indigo-600 text-sm font-bold mt-0.5">{businessConfig.phone}</p>
                <p className="text-gray-400 text-xs mt-0.5">Tap to call directly</p>
              </div>
              <ChevronRight size={20} className="text-indigo-400 group-hover:text-indigo-600 transition-colors"/>
            </a>

            {/* ── EMAIL ── */}
            <a href={`mailto:${businessConfig.email}?subject=Service Enquiry&body=Hi 1464 Country Cleaners, I would like to enquire about your services.`}
              className="flex items-center gap-4 p-4 rounded-2xl bg-purple-50 border-2 border-transparent hover:border-purple-300 hover:bg-purple-100 transition-all group cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md"><Mail className="text-white" size={20}/></div>
              <div className="flex-1">
                <p className="font-black text-sm text-gray-900">Send an Email</p>
                <p className="text-purple-600 text-sm font-bold mt-0.5">{businessConfig.email}</p>
                <p className="text-gray-400 text-xs mt-0.5">We reply within 24 hours</p>
              </div>
              <ChevronRight size={20} className="text-purple-400 group-hover:text-purple-600 transition-colors"/>
            </a>

            {/* ── DROP OFF ── */}
            <a href={businessConfig.mapsUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-2xl bg-orange-50 border-2 border-transparent hover:border-orange-300 hover:bg-orange-100 transition-all group cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md"><MapPin className="text-white" size={20}/></div>
              <div className="flex-1">
                <p className="font-black text-sm text-gray-900">Drop Off Your Garments</p>
                <p className="text-orange-600 text-sm font-bold mt-0.5">{businessConfig.address}</p>
                <p className="text-gray-400 text-xs mt-0.5">Tap to open in Google Maps</p>
              </div>
              <ChevronRight size={20} className="text-orange-400 group-hover:text-orange-600 transition-colors"/>
            </a>

            {/* ── HOURS ── */}
            <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 border-2 border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gray-700 flex items-center justify-center shrink-0 shadow-md"><Clock className="text-white" size={20}/></div>
              <div className="flex-1">
                <p className="font-black text-sm text-gray-900 mb-2">Opening Hours</p>
                <div className="space-y-1">
                  {Array.isArray(businessConfig.hours)
                    ? businessConfig.hours.map((h, i) => (
                        <div key={i} className={`flex justify-between text-xs ${h.time === "Closed" ? "text-red-400 font-bold" : "text-gray-600"}`}>
                          <span className="font-semibold">{h.day}</span>
                          <span>{h.time}</span>
                        </div>
                      ))
                    : <p className="text-gray-600 text-sm">{businessConfig.hours}</p>
                  }
                </div>
              </div>
            </div>

            <button onClick={() => setBookOpen(true)}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-base hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
              📅 Book a Free Pickup Now
            </button>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══════════════════════════════════════════ */}
      <Footer setBookOpen={setBookOpen} scrollTo={scrollTo}/>

      {/* ══ BOOKING MODAL ═══════════════════════════════════ */}
      {bookOpen && <BookingModal onClose={() => setBookOpen(false)}/>}

      {/* ══ BLOG MODAL ══════════════════════════════════════ */}
      {blogOpen && <BlogModal onClose={() => setBlogOpen(false)} onBook={() => { setBlogOpen(false); setBookOpen(true); }}/>}

    </div>
  );
}


// ── Contact Form (kept — not rendered but preserved) ──────────
function ContactForm({ onBook }) {
  const [form,    setForm]    = useState({ name:"", email:"", phone:"", message:"" });
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  }

  function validate() {
    const e = {};
    if (!form.name.trim())    e.name    = "Name is required";
    if (!form.message.trim()) e.message = "Message is required";
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSent(true); }, 1500);
  }

  if (sent) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <CheckCircle size={32} className="text-green-600"/>
        </div>
        <h3 className="font-black text-xl text-green-700">Message Sent!</h3>
        <p className="text-green-600 text-sm mt-2 leading-relaxed">Thank you for reaching out. We will get back to you within 24 hours.</p>
        <button onClick={() => { setSent(false); setForm({ name:"", email:"", phone:"", message:"" }); }}
          className="mt-5 text-indigo-600 text-sm font-bold hover:underline">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
      <h3 className="font-black text-xl text-gray-900 mb-1">Send Us a Message</h3>
      <p className="text-gray-500 text-sm mb-5">Fill in the form and we will get back to you shortly.</p>
      <div className="space-y-3">
        <div>
          <input name="name" value={form.name} onChange={handleChange} placeholder="Full Name *"
            className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors bg-white ${errors.name ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400"}`}/>
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>
        <div>
          <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address"
            className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors bg-white ${errors.email ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400"}`}/>
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
        <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Phone Number"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400 bg-white"/>
        <div>
          <textarea name="message" value={form.message} onChange={handleChange} placeholder="Your message... *" rows={4}
            className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors bg-white resize-none ${errors.message ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400"}`}/>
          {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
        </div>
        <button onClick={handleSubmit} disabled={loading}
          className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-black hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm">
          {loading ? "Sending…" : "Send Message →"}
        </button>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-gray-200"/>
          <span className="text-gray-400 text-xs font-semibold">OR</span>
          <div className="flex-1 h-px bg-gray-200"/>
        </div>
        <button onClick={onBook}
          className="w-full border-2 border-indigo-600 text-indigo-600 py-3.5 rounded-xl font-black hover:bg-indigo-50 transition-colors text-sm">
          📅 Book a Pickup Instead
        </button>
      </div>
    </div>
  );
}


// ── Blog Modal ────────────────────────────────────────────────
const BLOG_POSTS = [
  {
    icon:  "🧥",
    tag:   "Dry Cleaning",
    title: "5 Signs Your Suit Needs Professional Dry Cleaning",
    date:  "March 10, 2026",
    body:  "Most people wait too long before bringing their suits in for cleaning. A suit that looks clean may still carry odours, body oils and invisible stains that break down fabric over time. Look out for: a dull or flat appearance, collar discolouration, persistent odour even after airing, visible stains on cuffs or lapels, and a loss of shape or structure. Dry cleaning restores all of this and extends the life of your suit significantly.",
  },
  {
    icon:  "👕",
    tag:   "Laundry Tips",
    title: "Why Your Clothes Are Shrinking in the Wash",
    date:  "March 5, 2026",
    body:  "Shrinkage is one of the most common laundry complaints and it is almost always avoidable. The main causes are washing at too high a temperature, over-drying in a hot tumble dryer, and agitation from a machine that is too full. Natural fibres like cotton, wool and linen are most vulnerable. Always check the care label, wash at 30 degrees where possible, and remove clothes from the dryer slightly damp. Better still — let us handle it.",
  },
  {
    icon:  "🧹",
    tag:   "Stain Removal",
    title: "What To Do in the First 60 Seconds After a Spill",
    date:  "February 28, 2026",
    body:  "The first minute after a spill is the most important. Act immediately: blot — never rub — with a clean cloth to absorb as much liquid as possible. Rubbing pushes the stain deeper into the fibres and spreads it. Do not apply heat — hot water can permanently set many stains. Rinse with cold water from behind the fabric to push the stain out. Then bring it to us as quickly as possible. The faster we treat it, the better the result.",
  },
  {
    icon:  "🛏️",
    tag:   "Bedding Care",
    title: "How Often Should You Professionally Wash Your Duvet?",
    date:  "February 20, 2026",
    body:  "Most people wash their duvets far less often than they should. The recommendation from sleep hygiene experts is every 3 to 6 months. Over time duvets accumulate sweat, dead skin cells, dust mites and allergens — even with a duvet cover. A home washing machine cannot fully saturate and clean a duvet, and incomplete drying causes mould inside the filling. A professional duvet wash every season keeps your sleep environment genuinely fresh and allergen-free.",
  },
  {
    icon:  "👔",
    tag:   "Care Tips",
    title: "The Right Way to Store Seasonal Clothing",
    date:  "February 12, 2026",
    body:  "Before storing seasonal clothes always have them professionally cleaned first. Invisible stains and body oils oxidise over months of storage and become permanent. Store garments in breathable cotton bags — never plastic, which traps moisture and causes mildew. Use cedar blocks instead of mothballs to deter insects without the chemical smell. Fold knitwear rather than hanging — hanging causes heavy knits to stretch out of shape. Bring your seasonal items to us before storage for the best results.",
  },
];

function BlogModal({ onClose, onBook }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="font-black text-xl text-gray-900">{selected ? selected.title : "Garment Care Blog"}</h2>
            <p className="text-gray-400 text-xs mt-0.5">{selected ? selected.date : "Expert tips from our cleaning specialists"}</p>
          </div>
          <button onClick={selected ? () => setSelected(null) : onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={20}/>
          </button>
        </div>
        <div className="p-6">
          {!selected && (
            <div className="space-y-4">
              {BLOG_POSTS.map((post, i) => (
                <button key={i} onClick={() => setSelected(post)}
                  className="w-full text-left p-5 rounded-2xl border-2 border-gray-100 hover:border-indigo-300 hover:shadow-md transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform">{post.icon}</div>
                    <div className="flex-1 min-w-0">
                      <span className="text-indigo-600 text-xs font-bold uppercase tracking-wide">{post.tag}</span>
                      <h3 className="font-black text-gray-900 text-base mt-0.5 leading-tight">{post.title}</h3>
                      <p className="text-gray-400 text-xs mt-1">{post.date}</p>
                      <p className="text-gray-500 text-sm mt-2 leading-relaxed line-clamp-2">{post.body}</p>
                    </div>
                    <ChevronRight size={18} className="text-gray-300 group-hover:text-indigo-500 transition-colors shrink-0 mt-1"/>
                  </div>
                </button>
              ))}
            </div>
          )}
          {selected && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-3xl">{selected.icon}</div>
                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">{selected.tag}</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{selected.body}</p>
              <div className="bg-indigo-50 rounded-2xl p-5">
                <p className="font-black text-sm text-indigo-700 mb-1">Need help with your garments?</p>
                <p className="text-indigo-600 text-sm">Our specialists are ready to help. Book a free pickup today.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={onBook} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black text-sm hover:bg-indigo-700 transition-colors">📅 Book a Pickup</button>
                <button onClick={() => setSelected(null)} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors">← Back to Blog</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}